<script>
  import "./i18n.js";
  import { isLoading } from "svelte-i18n";
  import { Router, Route } from "svelte-routing";
  import { SvelteToast } from "@zerodevx/svelte-toast";
  import Clarity from "@microsoft/clarity";
  import {
    PRODUCTION_HOSTNAMES,
    REGISTRATION_HOSTNAMES,
    REGISTRATION_URL,
    isReferralPortal,
    isDynamicReferralPage,
  } from "./utils/menu";
  import { clarityProjectId } from "./utils/analytics.js";
  import { initBranchWithConsent } from "lib/modules/Branch.svelte";
  import Home from "routes/Home.svelte";
  import Contact from "routes/Contact.svelte";
  import LivePricing from "routes/LivePricing.svelte";
  import Legal from "routes/Documents/Legal.svelte";
  import GenericDocs from "routes/Documents/Generic.svelte";
  import Register from "routes/Register.svelte";
  import GiftCards from "routes/GiftCards.svelte";
  import Referral from "routes/Referral.svelte";
  import ComingSoonCNY from "routes/ComingSoon/CNY.svelte";

  import CookieConsent from "lib/components/CookieConsent.svelte";
  import { cookieConsentStore } from "lib/stores/cookieConsent.js";

  const isRegistrationPage = REGISTRATION_HOSTNAMES.includes(
    window.location?.hostname,
  );

  const windowHostname = window.location?.hostname || "localhost";
  const renderAnalytics =
    isProduction &&
    (PRODUCTION_HOSTNAMES.some((h) => windowHostname.endsWith(h)) ||
      isRegistrationPage);

  const isReferral = isReferralPortal();
  const isDynamicReferral = isDynamicReferralPage();

  if (isRegistrationPage && !isDynamicReferral && !isDynamicReferral) {
    window.location.href = REGISTRATION_URL;
  }

  const options = {
    theme: {
      "--toastMsgPadding": "20px 20px",
    },
  };

  if (clarityProjectId && renderAnalytics) {
    Clarity.init(clarityProjectId);
    console.log("Microsoft Clarity initialized with ID:", clarityProjectId);
  }

  export let url = "";

  // Track consent state
  let hasAnalyticsConsent = true;
  let hasAdvertisingConsent = true;
  let hasFunctionalConsent = true;
  let analyticsLoaded = false;
  let advertisingLoaded = false;
  let functionalLoaded = false;

  // Subscribe to consent changes
  cookieConsentStore.subscribe((state) => {
    const newAnalyticsConsent = state.preferences.analytics;
    const newAdvertisingConsent = state.preferences.advertising;
    const newFunctionalConsent = state.preferences.necessary;

    // Only load if consent changed from false to true and not already loaded
    if (newAnalyticsConsent && !hasAnalyticsConsent && !analyticsLoaded) {
      loadAnalytics();
      analyticsLoaded = true;
    }

    if (newAdvertisingConsent && !hasAdvertisingConsent && !advertisingLoaded) {
      loadAdvertising();
      advertisingLoaded = true;
    }

    // Functional services (reCAPTCHA, Branch)
    if (newFunctionalConsent && !hasFunctionalConsent && !functionalLoaded) {
      loadFunctionalServices();
      functionalLoaded = true;
    }

    hasAnalyticsConsent = newAnalyticsConsent;
    hasAdvertisingConsent = newAdvertisingConsent;
    hasFunctionalConsent = newFunctionalConsent;
  });

  function loadFunctionalServices() {
    console.log("Loading functional services...");

    // Load reCAPTCHA
    const recaptchaScript = document.createElement("script");
    recaptchaScript.src = "https://www.google.com/recaptcha/api.js";
    recaptchaScript.async = true;
    document.head.appendChild(recaptchaScript);

    // Initialize Branch SDK
    initBranchWithConsent();
  }

  function loadAnalytics() {
    console.log("Loading analytics...");
    if (!renderAnalytics) return;

    console.log("Loading Google Analytics...");

    // Google Tag Manager
    (function (w, d, s, l, i) {
      w[l] = w[l] || [];
      w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
      var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l != "dataLayer" ? "&l=" + l : "";
      j.async = true;
      j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, "script", "dataLayer", "GTM-NS6NKP53");

    // Google Analytics
    const gtagScript = document.createElement("script");
    gtagScript.async = true;
    gtagScript.src = "https://www.googletagmanager.com/gtag/js?id=G-2NWTM6KPJD";
    document.head.appendChild(gtagScript);

    gtagScript.onload = () => {
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        dataLayer.push(arguments);
      }
      window.gtag = gtag;
      gtag("js", new Date());
      gtag("config", "G-2NWTM6KPJD");

      gtag("consent", "update", {
        analytics_storage: "granted",
      });

      console.log("Google Analytics loaded and consent granted");
    };
  }

  function loadAdvertising() {
    console.log("Loading advertising...");
    if (!renderAnalytics) return;

    console.log("Loading Facebook Pixel...");

    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      "script",
      "https://connect.facebook.net/en_US/fbevents.js",
    );

    window.fbq("init", "2399929130387488");
    window.fbq("track", "PageView");

    console.log("Facebook Pixel loaded");
  }
</script>

<SvelteToast {options} />

{#if $isLoading}
  <div class="i18n-loading">
    <div class="loading-spinner"></div>
    <p>Loading...</p>
  </div>
{:else}
  <!-- Only render CookieConsent after i18n is loaded -->
  <!-- <CookieConsent /> -->

  <Router {url}>
    <div class="root">
      {#if renderAnalytics && hasAnalyticsConsent}
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-NS6NKP53"
            height="0"
            width="0"
            style="display:none;visibility:hidden"
          ></iframe>
        </noscript>
      {/if}

      {#if renderAnalytics && hasAdvertisingConsent}
        <noscript>
          <img
            height="1"
            width="1"
            style="display:none"
            src="https://www.facebook.com/tr?id=2399929130387488&ev=PageView&noscript=1"
          />
        </noscript>
      {/if}

      <section>
        <div class="content">
          <Route path="/legal/*"><Legal /></Route>
          <Route path="/docs/*"><GenericDocs /></Route>
          <Route path="/register"><Register /></Route>
          <Route path="/gift-cards"><GiftCards /></Route>
          <Route path="/contact-us"><Contact /></Route>
          <Route path="/live-pricing"><LivePricing /></Route>
          <Route path="/cny"><ComingSoonCNY /></Route>
          {#if isReferral}
            <Route path="/"><Referral /></Route>
            <Route path="*"><Referral /></Route>
          {/if}
          {#if !isDynamicReferral && !isReferral}
            <Route path="/"><Home /></Route>
            <Route path="*"><Home /></Route>
          {/if}
          {#if isDynamicReferral}
            <Route path="/"><ComingSoonCNY /></Route>
            <Route path="*"><ComingSoonCNY /></Route>
          {/if}
        </div>
      </section>
    </div>
  </Router>
{/if}

<style lang="scss">
  @import "./styles/global.scss";

  .root {
    width: 100%;
    padding: 0;
    overflow-x: hidden;
    font-family: "Heebo", "sans-serif";
    font-style: normal;
    font-weight: 400;
    font-size: 15px;
    > section > div.content {
      display: flex;
      flex-direction: column;
    }
  }

  .i18n-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    background: linear-gradient(135deg, #1a0f1c 0%, #2c1810 100%);
    color: #f6efd3;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid rgba(210, 165, 82, 0.3);
    border-top: 4px solid #d2a552;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  .i18n-loading p {
    font-size: 1.1rem;
    font-weight: 500;
    margin: 0;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
